import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import LocationsWeServe from '../components/LocationsWeServe';

const STEPS = [
  { title: 'Create Your Free Profile', text: 'Setting up an account on our dating website in North Carolina will only take a few minutes. All you have to do is add a few recent photos and write a short, honest bio that clearly describes your routine, hobbies, and relationship goals.' },
  { title: 'Browse North Carolina Singles', text: 'Look for North Carolina singles in your city or surrounding areas using direct location, age, and lifestyle filters.' },
  { title: 'Connect and Meet Up', text: 'Signal interest, send a message, and build rapport through smooth, ongoing conversation. Once you are done establishing steady communication, choose a comfortable public spot for your first date. Keep it low-pressure on both sides.' },
];

const FREE_VS_PAID = [
  { feature: 'Create Profile & Upload Photos', free: 'Included', paid: 'Included' },
  { feature: 'Browse Local Members', free: 'Full access', paid: 'Full access' },
  { feature: 'Location & Interest Filters', free: 'Basic filters', paid: 'Advanced filters' },
  { feature: 'Send Likes & Express Interest', free: 'Daily limit', paid: 'Unlimited' },
  { feature: 'Direct Messaging', free: 'Introductory tools', paid: 'Full messaging access' },
  { feature: 'Love Diamond™ Interest Signals', free: 'Included', paid: 'Real-time tracking' },
];

const CITIES = [
  { name: 'Charlotte Singles and Dating', text: "As the state's commercial center, Charlotte has a fast-paced banking, corporate, and awesome nightlife environment. Charlotte singles balance long work hours with dining in South End, Uptown, and Plaza Midwood, making location-based filters useful for meeting people nearby." },
  { name: 'Raleigh and Durham Dating', text: 'Driven by tech hubs, research institutions, and major universities, Raleigh dating attracts educated professionals and researchers. Locals meet over a cup of coffee, go to neighborhood breweries, or spend weekends on the Raleigh greenways.' },
  { name: 'Greensboro and Winston-Salem Singles', text: 'In the Piedmont Triad, Greensboro singles and Winston-Salem residents enjoy a steady, community-oriented pace. Dates here revolve around historic downtown walks, local art galleries, and casual dining.' },
  { name: 'Asheville and Wilmington Singles', text: 'From mountain trails to Atlantic beaches, Asheville and Wilmington singles absolutely love an active lifestyle. Great matches meet each other over coastal seafood, beach walks, live music, or hiking along the Blue Ridge.' },
];

const FEATURES = [
  { title: 'Smart Matching and Filters', text: 'Our dating website in North Carolina includes practical search filters which you can use to filter matches by distance, age, shared interests, and core values, it helps you identify true compatibility much before.' },
  { title: 'Verified Profiles and Safety', text: 'Community trust is built on clear standards. We at SLAMMMM combine moderation with user reporting to maintain an environment focused on real North Carolina singles seeking real and genuine connections.' },
  { title: 'Mobile Access', text: 'You can use your account on desktop and mobile browsers. Track interest signals, check profile updates, and respond to messages whenever you get enough time.' },
];

const FIRST_DATE_IDEAS = [
  { title: 'Mountain Views', text: 'Grab coffee and take a beautiful walk on a short trail near the Blue Ridge Parkway in Asheville.' },
  { title: 'Coastal Walks', text: 'Explore the pier or boardwalk at Wrightsville Beach near Wilmington.' },
  { title: "Local Breweries", text: "Meet for a drink in Charlotte's NoDa district or downtown Raleigh." },
  { title: 'Vineyard Visits', text: 'Sample local wines along the Yadkin Valley trail for an easy weekend afternoon date.' },
];

const CONVERSATION_STARTERS = [
  'Are you more of a Blue Ridge mountain person or an Outer Banks beach person?',
  "What's your go-to weekend coffee spot in town?",
  "What's one local spot or festival in NC you've been meaning to check out?",
];

const FAKE_PROFILE_FLAGS = [
  'Excuses to avoid a quick 2-minute video call before an in-person date.',
  'Any request for money, wire transfers, or financial assistance.',
  'Forcing you to leave the platform immediately after sending a first message.',
];

const FAQS = [
  {
    q: 'What is the best dating website in North Carolina?',
    a: 'SLAMMMM is a strong option for members seeking a transparent dating website in North Carolina. It offers real-time interest indicators like Love Diamond™ without requiring large upfront contracts.',
  },
  {
    q: 'Are there free dating sites in North Carolina?',
    a: 'Yes. Many services offer basic access. SLAMMMM lets you join for free to set up a profile, view local members, and test core communication features before upgrading.',
  },
  {
    q: 'How do I meet North Carolina singles near me?',
    a: 'Create an honest profile, adjust your search radius to fit your county or city, and send direct messages to local members who share your hobbies and goals.',
  },
  {
    q: 'Is online dating in North Carolina safe?',
    a: 'Yes, provided you follow standard safety habits: keep messages on the platform initially, complete a video chat before meeting, choose public date spots, and never send money to anyone online.',
  },
  {
    q: 'What are the best cities for dating in North Carolina?',
    a: 'Populated regions like Charlotte, Raleigh, Durham, Greensboro, Winston-Salem, Asheville, and Wilmington offer active dating pools spanning diverse professions and age groups.',
  },
  {
    q: 'How much does a dating website cost?',
    a: 'Basic profile setup and browsing are often free. Premium plans across standard platforms typically range between $15 and $50 per month depending on feature access and plan duration.',
  },
  {
    q: 'Can I call someone for help with my dating profile?',
    a: 'Yes. SLAMMMM provides direct phone support at 15-651-9268 to help you configure your account, answer feature questions, and navigate local profile search options.',
  },
];

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
});

const SERVICE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Online Dating in North Carolina',
  serviceType: 'Online Dating Service',
  description: 'SLAMMMM is an online dating platform for singles in North Carolina, helping people create profiles, explore potential matches, and connect with others based on compatibility, communication style, and relationship goals.',
  url: 'https://www.slammmm.com/north-carolina',
  provider: { '@type': 'Organization', name: 'SLAMMMM', url: 'https://www.slammmm.com/' },
  areaServed: {
    '@type': 'State',
    name: 'North Carolina',
    containedInPlace: { '@type': 'Country', name: 'United States' },
  },
  audience: { '@type': 'Audience', audienceType: 'Singles' },
  serviceOutput: { '@type': 'Thing', name: 'Online dating connections' },
});

const NorthCarolina: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="North Carolina Dating Website | Meet Singles Free"
        description="Find a trusted dating website in North Carolina. Browse North Carolina singles in Charlotte, Raleigh & more. Join for free or call today."
        canonical="https://www.slammmm.com/north-carolina"
        schemas={[FAQ_SCHEMA, SERVICE_SCHEMA]}
      />

      {/* Hero */}
      <section className="py-100 breadcrumb_section bg-light border-bottom">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <div className="section_title">
                <h1 className="mb-4">North Carolina Dating Website for Singles</h1>
              </div>
              <p className="font20 mb-4">
                Finding a reliable dating website in North Carolina doesn&rsquo;t have to mean spending hours swiping through profiles that look like bots or don&rsquo;t respond. SLAMMMM gives North Carolina singles a direct way to meet local members who value clear communication and have a genuine intent. Whether you live in a major metro like Charlotte, work in the Research Triangle, or stay in a coastal or mountain town, our platform is meant to simplify dating in North Carolina. You can set up an account for free, browse local members, share interest signals, and start real conversations without hidden charges.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/signup"><button className="btn btn-primary">Sign Up for Free</button></Link>
                <a href="tel:156519268"><button className="btn btn-outline-primary">Call Us: 15-651-9268</button></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dating in NC */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Dating in North Carolina</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Why Try Online Dating</h3>
              <p className="font20 mb-0">
                North Carolina covers distinct regions: the coastal plain, the central Piedmont, and the Blue Ridge Mountains. As major employment centers, military bases, and university towns are spread across long distances, meeting compatible people through your immediate work or local social circle can look like a real task. A dedicated dating website in North Carolina makes it super easy to expand your reach beyond your neighborhood, thus connecting you with members across town or in nearby places who share your values and lifestyle.
              </p>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Meet Singles Near You</h3>
              <p className="font20 mb-0">
                North Carolina has a substantial local network of people who are actively looking for dates, long-term partners, and serious relationships. Online dating in North Carolina helps you filter through those numbers and offers an approachable environment where you can expect to establish real connections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-100 our_approach_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">How Our Dating Website Works</h2>
            </div>
          </div>

          <div className="row g-4">
            {STEPS.map((step, i) => (
              <div className="col-md-4" key={step.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font22 mb-0">{step.title}</h3>
                    <p className="font28 mb-0 text-primary opacity-50">{`0${i + 1}`}</p>
                  </div>
                  <p className="mb-0 font20">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Create Your Free Account Today</button></Link>
          </div>
        </div>
      </section>

      {/* Free dating sites */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Free Dating Sites in North Carolina</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-5">
              <h3 className="font22 mb-3">What Free Members Get</h3>
              <p className="font20 mb-0">
                Many free dating sites in North Carolina let you create a profile, only to block basic communication behind charges when you try to send a message and talk to someone. SLAMMMM sets clear expectations at the initial stage itself. When you join for free, you get full access to build your profile, upload photos, apply search filters, and browse local members across the state.
              </p>
            </div>
            <div className="col-lg-7">
              <h3 className="font22 mb-3">Free vs. Paid Features</h3>
              <p className="font20 mb-3">Here is how features break down between basic access and premium tiers:</p>
              <div className="table-responsive-blog">
                <table>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Free Access</th>
                      <th>Premium Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FREE_VS_PAID.map((row) => (
                      <tr key={row.feature}>
                        <td>{row.feature}</td>
                        <td>{row.free}</td>
                        <td>{row.paid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="border-top pt-4 mt-4">
            <h3 className="font22 mb-3">Hidden Costs to Avoid</h3>
            <p className="font20 mb-0">
              When you evaluate free dating sites in North Carolina, always watch for platforms that lock essential messaging tools, auto-renew subscriptions without prior notice, or keep inactive profiles visible. We keep free dating access transparent so you can view local profiles without upfront commitments.
            </p>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">North Carolina Singles by City</h2>
            </div>
          </div>

          <div className="row g-4">
            {CITIES.map((city) => (
              <div className="col-md-6" key={city.name}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom text-primary">{city.name}</h3>
                  <p className="mb-0 font20">{city.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Features of Our Dating Website</h2>
            </div>
          </div>

          <div className="row g-4">
            {FEATURES.map((f) => (
              <div className="col-md-4" key={f.title}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom">{f.title}</h3>
                  <p className="mb-0 font20">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dating tips */}
      <section className="py-100 our_approach_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Dating Tips in North Carolina</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-7">
              <h3 className="font22 mb-3">First Date Ideas in NC</h3>
              <p className="font20 mb-3">It&rsquo;s always a good idea to keep initial meetings casual and just be comfortable with each other:</p>
              <ul className="font20">
                {FIRST_DATE_IDEAS.map((t) => (
                  <li key={t.title}><strong>{t.title}:</strong> {t.text}</li>
                ))}
              </ul>
            </div>
            <div className="col-lg-5">
              <h3 className="font22 mb-3">Easy Conversation Starters</h3>
              <ul className="font20">
                {CONVERSATION_STARTERS.map((c) => <li key={c}>&ldquo;{c}&rdquo;</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Safe Online Dating Tips</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Spot Fake Profiles</h3>
              <p className="font20 mb-3">Prioritize your safety on any dating website in North Carolina by watching for these red flags:</p>
              <ul className="font20">
                {FAKE_PROFILE_FLAGS.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Meeting in Person Safely</h3>
              <p className="font20 mb-0">
                Choose well-lit, public venues for your initial dates, because that&rsquo;s when both of you will get to know each other. Drive yourself to and from the location, and inform a trusted friend or family member about where you are going, who you are meeting, and when you expect to return. You can also share your live location with them, just in case.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Create a standout profile */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Create a Standout Profile</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Photos That Work</h3>
              <p className="font20 mb-0">
                Upload 3 to 4 recent, clear photos. You can use a solo headshot where you are smiling naturally, and add a secondary photo that shows you enjoying a favorite hobby or spending time outdoors.
              </p>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Write an Honest Bio</h3>
              <p className="font20 mb-0">
                Keep your description concise and grounded. Mention what you enjoy on your days off, how you like to communicate, and what you want in a partner to give North Carolina singles a clear sense of who you actually are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dating advisor */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Talk to a Dating Advisor</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Get Help by Phone</h3>
              <p className="font20 mb-0">
                If you want some help setting up your account or selecting pictures, you don&rsquo;t have to figure out online dating in North Carolina on your own. We offer direct phone support to assist with profile setup and navigation.
              </p>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Matchmaking Support Options</h3>
              <p className="font20 mb-0">
                Our support team answers questions about how features work on our dating website in North Carolina. We help you present your profile clearly so you can browse and message local members with confidence.
              </p>
            </div>
          </div>

          <div className="text-center mt-5">
            <a href="tel:156519268"><button className="btn btn-primary">Call Our Support Team: 15-651-9268</button></a>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Why Choose Our Dating Website</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3 pb-2 border-bottom">Real Member Success Stories</h3>
              <p className="mb-0 font20">
                Members across the state use SLAMMMM to avoid meaningless messaging. By using transparent interest tools like Love Diamond™, local members get to know mutual interest early, and there&rsquo;s a smooth transition from online chats to real dates.
              </p>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3 pb-2 border-bottom">Trust and Privacy Standards</h3>
              <p className="mb-0 font20">
                We protect your personal information using standard encryption protocols and clear data privacy practices, giving you a secure space to connect with North Carolina singles.
              </p>
            </div>
          </div>
        </div>
      </section>

      <LocationsWeServe />

      {/* Final CTA */}
      <section className="py-100 love_journey_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center z-3 position-relative">
              <div className="section_title mb-3">
                <h2 className="text-white">Start Meeting NC Singles Today</h2>
              </div>
              <p className="text-white font20 mb-4 pb-2">
                Skip the messaging loops and connect with real people. Join thousands of North Carolina singles on a dating website in North Carolina designed for practical communication and mutual clarity. Create your profile now or call our team for help getting started.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/signup"><button className="btn btn-primary">Sign Up Free Now</button></Link>
                <a href="tel:156519268"><button className="btn btn-outline-light">Call Us: 15-651-9268</button></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-100 help_faq_section" id="north-carolina-faq">
        <div className="container">
          <div className="section_title text-center mb-5">
            <h2 className="mb-0">North Carolina Dating FAQs</h2>
          </div>

          <div className="row">
            <div className="col-lg-9 mx-auto">
              <div className="accordion" id="north-carolina-faq-accordion">
                {FAQS.map((item, index) => (
                  <div className="accordion-item" key={item.q}>
                    <h3 className="accordion-header">
                      <button
                        className={`accordion-button ${index === 0 ? '' : 'collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#north-carolina-faq-${index}`}
                        aria-expanded={index === 0 ? 'true' : 'false'}
                        aria-controls={`north-carolina-faq-${index}`}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`north-carolina-faq-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#north-carolina-faq-accordion"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NorthCarolina;
